import {
  AppShell,
  Box,
  Burger,
  Container,
  Drawer,
  Grid,
  Group,
  Modal,
  Text,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { safeApiCall } from "api/common.service";
import { getNotification } from "api/notification.service";
import ForgotPassword from "components/auth/ForgotPassword";
import { Login } from "components/auth/Login";
import DefaultHeader from "components/header/DefaultHeader";
import HeaderActions from "components/header/HeaderActions";
import HeaderSearchBox from "components/header/HeaderSearchBox";
import { SafeImage } from "components/SafeImage ";
import { DefaultNavLinks } from "pages/NavLinks";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCartStore } from "stores/useCartStore";
import { useProductStore } from "stores/useProductStore";
import { useSessionStore } from "stores/userSessionStore";
import { COLORS } from "styles/colors";
import { SafeContent } from "utils/reactHelper";
import { FOOTER_HEIGHT, HEADER_HEIGHT, MAX_WIDTH } from "./consts";
import FooterSocialIcons from "./FooterSocialIcons";
import classes from "./MainLayout.module.css";

// Define the props for the MainLayout component
// This interface defines the structure of the props that the MainLayout component will receive
interface MainLayoutProps {
  children: React.ReactNode;
}
const POLL_INTERVAL = 10 * 60 * 1000; // 10 minutes

// PublicLayout component
// This component is used to render content for public pages, such as the home page or about page.
// It includes a header and a main section where the children components will be rendered.
const MainLayout = ({ children }: MainLayoutProps) => {
  const { isLoggedIn } = useSessionStore();
  const theme = useMantineTheme();

  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const [asideDrawerOpened, asideDrawer] = useDisclosure(false);
  // Add new state for nav drawer from right
  const [navDrawerOpened, navDrawer] = useDisclosure(false);
  const [mode, setMode] = useState<"login" | "forgot">("login");
  const [email, setEmail] = useState("");
  const navigate = useNavigate();
  const cartItems = useCartStore((state) => state.cart.items); // ✅ subscribe to cart
  const [notification, setNotification] = useState<string | null>(null);

  useEffect(() => {
    //if (!sessionVerified) return;

    const fetchNotification = async () => {
      try {
        const response = await safeApiCall(getNotification());
        setNotification(response?.message ?? null);
      } catch (err) {
        setNotification(null);
      }
    };

    fetchNotification(); // initial fetch
    // skip polling from hidden tabs - those requests tend to stall and time out
    const interval = setInterval(() => {
      if (!document.hidden) fetchNotification();
    }, POLL_INTERVAL);

    return () => clearInterval(interval); // cleanup
  }, []);

  const [loginState, setLoginState] = useState({
    username: "",
    password: "",
    rememberMe: false,
  });

  // Function to handle the login submission
  // This will close the drawer and set the logged-in state
  const handleLogin = async () => {
    asideDrawer.close();
  };
  // Function to handle the forgot password submission
  // This will send a reset password email to the provided email address
  const handleForgotPassword = () => {
    asideDrawer.close();
  };
  // Function to handle the forgot password action
  // This will prefill the email field with the username from the login state
  const onForgotPassword = () => {
    setEmail(loginState.username); // prefill the email field
    setMode("forgot");
  };

  const allProducts = useProductStore((s) => s.allProducts);
  const [searchValue, setSearchValue] = useState("");
  const [modalOpened, setModalOpened] = useState(false);
  const filteredProducts = allProducts.items.filter((p) =>
    p.title.toLowerCase().includes(searchValue.toLowerCase()),
  );
  const [popoverOpened, setPopoverOpened] = useState(false);

  return (
    <Container className={classes.mainBody} fluid>
      <AppShell
        header={{ height: HEADER_HEIGHT }}
        footer={{ height: FOOTER_HEIGHT }}
        padding="md"
      >
        <AppShell.Header className={classes.header}>
          {/* 🔝 Top Row: Logo + Burger + Login Actions */}
          <Container
            size={MAX_WIDTH}
            h={HEADER_HEIGHT / 2}
            px="md"
            className={classes.mainBodyContent}
          >
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center", // 👈 Vertically center children
                height: "100%", // Ensure the box takes full height of the row
              }}
            >
              {/* Left side: Burger + Logos */}
              <Group align="center">
                <Burger
                  opened={navDrawerOpened}
                  onClick={navDrawer.toggle}
                  hiddenFrom="sm"
                  size="sm"
                />
                <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <SafeImage
                    src="/images/logo-sm.png"
                    hiddenFrom="sm"
                    width={40}
                    height={40}
                    style={{ objectFit: "contain" }}
                  />
                  <SafeImage
                    src="/images/logo.png"
                    visibleFrom="sm"
                    width={200}
                    height={61}
                    fit="contain"
                    style={{ objectFit: "contain" }}
                  />
                </Box>
              </Group>
              <HeaderActions
                onLoginClick={() => {
                  setMode("login");
                  asideDrawer.open();
                }}
              />
            </Box>
          </Container>
          <Box
            className={classes.mainBodyContent}
            style={{
              height: "1px",
              backgroundColor: "var(--mantine-color-gray-3)", // 👈 Adjust to your theme
            }}
          />

          {/* 🧭 Bottom Row: Navigation Links + Search */}
          {/* <Container
            size={MAX_WIDTH}
            h={HEADER_HEIGHT / 2}
            px="md"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between", // Centers DefaultHeader horizontally
              position: "relative", // Needed for absolute positioning
            }}
          > */}
          <Container h={HEADER_HEIGHT / 2} className={classes.mainBodyContent}>
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center", // 👈 Vertical centering
                width: "100%",
                height: "100%", // Match container height
              }}
            >
              <Box
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {/* Nav links to the left */}
                <DefaultHeader />
              </Box>
              <Box style={{ flexShrink: 0 }}>
                {/* Search box stays on far right */}
                <HeaderSearchBox />
              </Box>
            </Box>
          </Container>
        </AppShell.Header>
        <AppShell.Main>
          {/* Full-width notification bar */}
          {notification && (
            <>
              <Box
                fz="sm"
                style={{
                  width: "100vw",
                  marginLeft: "calc(-50vw + 50%)", // cancel out centered layout
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                  backgroundColor: isDark ? theme.colors.dark[6] : "#fa6831",
                  color: isDark ? theme.colors.gray[0] : "#fff",
                  padding: "0.75rem 1rem",
                  borderBottom: `1px solid ${
                    isDark ? theme.colors.dark[4] : theme.colors.gray[3]
                  }`,
                }}
              >
                <SafeContent html={notification} />
              </Box>
            </>
          )}

          <Container fluid className={classes.mainBodyContent} px="md">
            {children}
          </Container>
        </AppShell.Main>
        <AppShell.Footer style={{ width: "100%", zIndex: 1000 }}>
          <Container className={classes.mainFooterContent}>
            <FooterSocialIcons />
            <Box
              style={{
                marginLeft: "0.75rem",
                fontSize: "0.75rem",
                color: "#888",
              }}
            >
              © {new Date().getFullYear()} John Jacobson Limited. All rights
              reserved.
            </Box>
            <Box
              style={{
                position: "absolute",
                bottom: "0",
                left: "1rem",
                fontSize: "0.75rem",
                color: "#888",
              }}
            >
              Version: {process.env.REACT_APP_BUILD_VERSION}
              {` - (built ${new Date(process.env.REACT_APP_BUILD_TIME ?? "").toLocaleString("en-NZ")})`}
            </Box>
          </Container>
        </AppShell.Footer>

        {/* left drawer */}
        <Drawer
          opened={navDrawerOpened}
          onClose={navDrawer.close}
          position="left"
          size="xs"
          padding="md"
          title="Menu"
          overlayProps={{ backgroundOpacity: 0.5, blur: 2 }}
        >
          {/* 👋 Friendly welcome */}
          {isLoggedIn && (
            <Box mb="md" style={{ fontSize: 16, fontWeight: 500 }}>
              👋 Welcome back, friend!
            </Box>
          )}
          <Box mt="xl" visibleFrom="xs" style={{ width: "100%" }}>
            <img
              src="/images/login2.jpg"
              alt="Drawer footer"
              style={{
                width: "100%",
                objectFit: "cover",
                borderRadius: "8px",
              }}
            />
          </Box>
          <Box className={classes.drawerNav}>
            {DefaultNavLinks.map(({ link, label, icon: Icon }) => (
              <Group
                key={label}
                onClick={() => {
                  navigate(link);
                  navDrawer.close();
                }}
                className={classes.drawerLink}
              >
                <Icon size={18} stroke={1.5} color={COLORS.colorOrange} />
                <span>{label}</span>
              </Group>
            ))}
          </Box>
        </Drawer>
        {/* aside drawer: login */}
        <Drawer
          opened={asideDrawerOpened}
          onClose={asideDrawer.close}
          title={mode === "login" ? "Login" : "Reset Password"}
          position="right"
          size="md"
          overlayProps={{ backgroundOpacity: 0.5, blur: 4 }}
        >
          <Box mt="xl" visibleFrom="xs" style={{ width: "100%" }}>
            <img
              src="/images/login2.jpg"
              alt="Drawer footer"
              style={{
                width: "100%",
                objectFit: "cover",
                borderRadius: "8px",
              }}
            />
          </Box>

          {mode === "login" ? (
            <Login
              values={loginState}
              setValues={(v) => setLoginState((prev) => ({ ...prev, ...v }))}
              onSubmit={handleLogin}
              onForgotPassword={onForgotPassword}
            />
          ) : (
            <ForgotPassword
              email={email}
              setEmail={setEmail}
              onSubmit={handleForgotPassword}
              onBack={() => setMode("login")}
            />
          )}
        </Drawer>

        <Modal
          opened={modalOpened}
          onClose={() => setModalOpened(false)}
          title={`Search results for "${searchValue}"`}
          size="lg"
          centered
          overlayProps={{ backgroundOpacity: 0.5, blur: 3 }}
        >
          {filteredProducts.length === 0 ? (
            <Text>No products found.</Text>
          ) : (
            <Grid>
              {filteredProducts.map((p) => (
                <Grid.Col key={p.productCode} span={6}>
                  <Group align="center">
                    <SafeImage
                      src={`/images/${p.featuredImageName || "no-image.png"}`}
                      width={48}
                      height={48}
                      radius="md"
                    />
                    <Text fw={500}>{p.title}</Text>
                  </Group>
                </Grid.Col>
              ))}
            </Grid>
          )}
        </Modal>
      </AppShell>
    </Container>
  );
};

export default MainLayout;
